// --- Constants ----------------------------------------------------------------------------------

/**
 * Everything is read in Cairo time. The backend groups its series with an explicit timezone, so a date
 * rendered in the browser's own zone would disagree with the chart beside it for anyone travelling.
 */
const TIMEZONE = "Africa/Cairo";

// --- Logic --------------------------------------------------------------------------------------

const localeOf = (lang: string | null): string => (lang === "en" ? "en-EG" : "ar-EG");

export function formatNumber(value: number, lang: string | null): string {
    return (value ?? 0).toLocaleString(localeOf(lang));
}

/** The number and its unit. `egp` is a unit, not a word, so this is not a translated sentence. */
export function formatMoney(value: number, lang: string | null, unit: string): string {
    return `${formatNumber(Math.round(value ?? 0), lang)} ${unit}`;
}

export function formatPercent(value: number, lang: string | null): string {
    return `${(value ?? 0).toLocaleString(localeOf(lang))}%`;
}

export function formatMs(value: number, lang: string | null): string {
    return `${formatNumber(value, lang)}ms`;
}

/** Day and month — the density a table row can carry without pushing its neighbours out. */
export function formatDate(value: string | null | undefined, lang: string | null): string {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat(localeOf(lang), { timeZone: TIMEZONE, day: "numeric", month: "short", year: "numeric" }).format(date);
}

/** The same date with a clock, for rows where the hour is the point — logs and tickets. */
export function formatDateTime(value: string | null | undefined, lang: string | null): string {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return new Intl.DateTimeFormat(localeOf(lang), {
        timeZone: TIMEZONE,
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(date);
}
